import {getChapterIndexByTime} from '@components/LiveEvent/RecordingsPlayer/@utils/getChapterIndexByTime';
import {clamp} from '@utils/clamp';
import {getBaseRemSize} from '@utils/getBaseRemSize';
import {IChapterCue} from '@utils/parseChapterMarks';
import {useCallback, useMemo, useRef, useState} from 'react';

export const useChapterHoverTracker = (
    cues: IChapterCue[],
    duration: number,
) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [cursorX, setCursorX] = useState<number | null>(null);

    const handleMouseMove = (e: React.MouseEvent) => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const {left, width} = container.getBoundingClientRect();
        const thumbWidth = 0.75 * getBaseRemSize();
        const percentageLeftInCue = 1 - (e.clientX - left) / width;
        const offset = thumbWidth * percentageLeftInCue;

        const x = clamp(e.clientX - left - offset, 0, width);

        setCursorX(x);
    };

    const updateCursorFromTime = useCallback(
        (time: number) => {
            const container = containerRef.current;
            if (!container || duration === 0) {
                return;
            }

            const {width} = container.getBoundingClientRect();
            const x = (time / duration) * width;
            setCursorX(Math.max(0, Math.min(x, width)));
        },
        [duration],
    );

    const hoveredTime = useMemo(() => {
        if (cursorX == null || duration <= 0 || !containerRef.current) {
            return null;
        }

        const {width} = containerRef.current.getBoundingClientRect();

        if (width <= 0) {
            return null;
        }

        return Math.min(duration, (cursorX / width) * duration);
    }, [cursorX, duration]);

    const hoveredChapterIndex = useMemo(() => {
        if (hoveredTime == null || !cues?.length) {
            return null;
        }
        return getChapterIndexByTime(hoveredTime, cues);
    }, [hoveredTime, cues]);

    const reset = () => setCursorX(null);

    return {
        ref: containerRef,
        cursorX,
        hoveredTime,
        hoveredChapterIndex,
        handleMouseMove,
        handleMouseLeave: reset,
        setHoverByTime: updateCursorFromTime,
    };
};
